import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DATE_QUICK_PICKS, formatDocDate, parseDocDate } from './smartFields';

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

interface DateFieldPickerProps {
  /** Heading shown above the picker, e.g. "Follow-up date". */
  label?: string;
  /** The date currently in the document, when changing an existing one. */
  current?: string | null;
  /** Receives the date formatted the app's way: "28 Sep 2026". */
  onPick: (text: string) => void;
}

/**
 * Quick picks (Today, +3 days, +1 week, +2 weeks, +1 month) plus a calendar
 * input — shared by date fill-in fields and the 📅 button beside dates in the
 * document (docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2 C11).
 */
export function DateFieldPicker({ label, current, onPick }: DateFieldPickerProps) {
  const existing = current ? parseDocDate(current) : null;
  const [iso, setIso] = useState(existing ? toIsoDate(existing) : '');
  const today = new Date();

  return (
    <div className="flex w-64 flex-col gap-2">
      {label && <div className="text-xs font-semibold text-ink">{label}</div>}
      <div className="flex flex-wrap gap-1">
        {DATE_QUICK_PICKS.map((pick) => (
          <Button key={pick.label} type="button" size="sm" variant="ghost" onClick={() => onPick(formatDocDate(pick.make(today)))}>
            {pick.label}
          </Button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input type="date" value={iso} onChange={(e) => setIso(e.target.value)} aria-label={label ? `${label} — pick a date` : 'Pick a date'} />
        <Button
          type="button"
          size="sm"
          disabled={!iso}
          onClick={() => {
            const picked = parseDocDate(iso);
            if (picked) onPick(formatDocDate(picked));
          }}
        >
          Insert
        </Button>
      </div>
    </div>
  );
}
